import { Box } from "@chakra-ui/react";
import { GroupBase, Size } from "chakra-react-select";
import { ReactElement } from "react";
import { FieldPath, FieldValues } from "react-hook-form";

import { ControlledInput, ControlledInputProperties } from "../ControlledInput/ControlledInput";
import { SearchSelect, SearchSelectProperties } from "./SearchSelect";

export type ControlledSearchSelectProps<
  T extends FieldValues,
  TName extends FieldPath<T>,
  IsMulti extends boolean = false,
> = {
  isEditable?: boolean;
  onDelete?: (data: { label: string; value: { id: string } }) => void;
  onSaveEdit?: (data: { label: string; value: { id: string } }) => void;
} & Omit<ControlledInputProperties<T, TName>, "render"> &
  Partial<Pick<SearchSelectProperties<any>, "onChange">> &
  Pick<
    SearchSelectProperties<any, IsMulti>,
    | "creatableFunction"
    | "creatableLabel"
    | "getOptionLabel"
    | "highlightResults"
    | "isClearable"
    | "isSearchable"
    | "onInputChange"
    | "options"
    | "renderOption"
    | "setValue"
    | "singleValue"
  >;

export const ControlledSearchSelect = <T extends FieldValues, TName extends FieldPath<T>>({
  creatableFunction,
  creatableLabel,
  getOptionLabel,
  highlightResults,
  isClearable,
  isEditable,
  isSearchable,
  onChange: componentOnChange,
  onDelete,
  onInputChange,
  onSaveEdit,
  options,
  renderOption: RenderOption,
  setValue,
  singleValue,
  size,
  ...controlledProperties
}: ControlledSearchSelectProps<T, TName>): ReactElement => {
  const isGroupBase = (o: GroupBase<T> | T): o is GroupBase<T> => {
    return (o as GroupBase<T>).options !== undefined;
  };
  return (
    <ControlledInput
      {...controlledProperties}
      render={({ onChange, value, ...field }) => {
        return (
          // TODO fix any
          <SearchSelect<any>
            creatableFunction={creatableFunction}
            creatableLabel={creatableLabel}
            highlightResults={highlightResults}
            isClearable={isClearable}
            isDisabled={controlledProperties.isDisabled}
            isEditable={!!isEditable}
            isSearchable={isSearchable}
            onChange={(value) => {
              componentOnChange?.(value?.value ?? null);
              onChange(value?.value ?? null);
            }}
            onDelete={onDelete}
            onInputChange={onInputChange}
            onSaveEdit={onSaveEdit}
            options={(options ?? []).map((option) => {
              if (isGroupBase(option)) {
                return {
                  label: option.label,
                  options: option.options.map((op) => ({
                    label: getOptionLabel?.(op) ?? "",
                    value: op,
                  })),
                };
              }
              return {
                label: getOptionLabel?.(option) ?? "",
                value: option,
              };
            })}
            renderOption={
              RenderOption
                ? ({ data, selectOption, ...rest }) => (
                    <Box
                      onClick={() =>
                        selectOption(
                          data.value == null
                            ? null
                            : {
                                label: getOptionLabel?.(data.value) ?? "",
                                value: data.value,
                              },
                        )
                      }
                    >
                      <RenderOption data={data.value} selectOption={selectOption} {...rest} />
                    </Box>
                  )
                : undefined
            }
            setValue={setValue}
            singleValue={singleValue}
            size={size as Size}
            value={
              value == null
                ? null
                : {
                    label: getOptionLabel?.(value) ?? "",
                    value,
                  }
            }
            {...field}
          />
        );
      }}
      size={size}
    />
  );
};
